import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Stack, Typography } from '@mui/material';

import { AppTab, AppTabs } from 'src/components/app-tabs';
import { AppDialog } from 'src/components/app-dialog';
import { AppButton, StackColumn, StackRow } from 'src/components';
import { IconArrowLeft, IconArrowRight, IconDot } from 'src/libraries/icons';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { PackageCodes } from 'src/contexts/SubscriptionsContext';
import { Block, ONBOARDING_PAGES } from './onboarding-dialog-config';

//
//

const DIALOG_CLOSE_DURATION_MS = 300;

//
// Block renderers — every page is `{ title, blocks }`; only the blocks vary in kind.
//

type BlockProps = { block: Block; stepIndex: number; onStepChange: (i: number) => void; onClose: () => void };

const PageView = ({ block, stepIndex, onStepChange, onClose }: BlockProps) => {
  const navigate = useNavigate();

  switch (block.type) {
    case 'paragraph':
      return (
        <Typography color={block.secondary ? 'text.secondary' : undefined}>{block.text}</Typography>
      );

    case 'bullets':
      return (
        <StackColumn gap={1}>
          {block.intro && (
            <Typography color="text.secondary" variant="overline">
              {block.intro}
            </Typography>
          )}

          {block.items.map((text) => (
            <Stack key={text} flexDirection="row" alignItems="flex-start">
              <Stack
                sx={{ width: 16, height: '1lh', overflow: 'hidden', flexShrink: 0, marginRight: 1 }}
                alignItems="center"
                justifyContent="center"
              >
                <IconDot size={16} style={{ transform: 'scale(3)', transformOrigin: 'center' }} />
              </Stack>
              <Typography>{text}</Typography>
            </Stack>
          ))}
        </StackColumn>
      );

    case 'steps':
      return (
        <StackColumn>
          <AppTabs value={stepIndex} onChange={(_, v) => onStepChange(v)}>
            {block.steps.map((step, i) => (
              <AppTab key={step.label} label={step.label} value={i} />
            ))}
          </AppTabs>
          <StackColumn>
            {block.steps[stepIndex].blocks.map((stepBlock, i) => (
              <PageView
                key={i}
                block={stepBlock}
                stepIndex={stepIndex}
                onStepChange={onStepChange}
                onClose={onClose}
              />
            ))}
          </StackColumn>
        </StackColumn>
      );

    case 'cta':
      return (
        <StackColumn gap={1} alignItems="center">
          <AppButton
            variant="contained"
            size="small"
            onClick={() => {
              navigate(PATH_DASHBOARD.programs.root);
              onClose();
            }}
          >
            {block.primaryLabel}
          </AppButton>

          {block.secondaryLabel && (
            <AppButton variant="text" size="small" onClick={onClose}>
              {block.secondaryLabel}
            </AppButton>
          )}
        </StackColumn>
      );
  }
};

//
//

const findStepsBlock = (blocks: Block[]) => blocks.find((b): b is Extract<Block, { type: 'steps' }> => b.type === 'steps');

//
//

type OnboardingDialogProps = {
  open: boolean;
  onClose: () => void;
  packageCode?: string;
};

export const ProgrammeOnboardingDialog: React.FC<OnboardingDialogProps> = ({
  open,
  onClose,
  packageCode,
}) => {
  const [page, setPage] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);

  const PAGES = (packageCode && ONBOARDING_PAGES[packageCode as PackageCodes]) || [];

  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setPage(0);
        setStepIndex(0);
      }, DIALOG_CLOSE_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    setStepIndex(0);
  }, [page]);

  if (!PAGES.length) return null;

  const currentPage = PAGES[page];
  const stepsBlock = findStepsBlock(currentPage.blocks);
  const canGoBackWithinPage = !!stepsBlock && stepIndex > 0;
  const canGoForwardWithinPage = !!stepsBlock && stepIndex < stepsBlock.steps.length - 1;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={currentPage.title}
      sx={{ '& .MuiDialogContent-root .MuiTypography-root': { fontSize: 14 } }}
      actions={
        <StackRow justifyContent="space-between" width="100%">
          <Typography color="text.secondary" variant="body2">
            {page + 1} of {PAGES.length}
          </Typography>

          <StackRow gap={1}>
            {page > 0 && (
              <AppButton
                variant="outlined"
                size="small"
                sx={{ gap: 1 }}
                onClick={() => {
                  if (canGoBackWithinPage) {
                    setStepIndex((s) => s - 1);
                  } else {
                    setPage((p) => p - 1);
                  }
                }}
              >
                <IconArrowLeft size={16} /> Previous
              </AppButton>
            )}

            {page < PAGES.length - 1 ? (
              <AppButton
                variant="contained"
                size="small"
                sx={{ gap: 1 }}
                onClick={() => {
                  if (canGoForwardWithinPage) {
                    setStepIndex((s) => s + 1);
                  } else {
                    setPage((p) => p + 1);
                  }
                }}
              >
                Next <IconArrowRight size={16} />
              </AppButton>
            ) : (
              <AppButton variant="contained" size="small" onClick={onClose}>
                Close
              </AppButton>
            )}
          </StackRow>
        </StackRow>
      }
    >
      <StackColumn>
        {currentPage.blocks.map((block, i) => (
          <PageView
            key={i}
            block={block}
            stepIndex={stepIndex}
            onStepChange={setStepIndex}
            onClose={onClose}
          />
        ))}
      </StackColumn>
    </AppDialog>
  );
};
